import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { AvailableModelSelector } from '@modules/ai-settings';
import type {
  LibraryEvidenceRef,
  LibraryEvidenceSelection,
  LibraryVariableResolution,
} from '@modules/shared-types';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

import { useLibraryAdaptationInspector } from '../hooks/useLibraryAdaptationInspector';

export interface LibraryAdaptationInspectorProps {
  projectId: string;
  strategyId: string;
  block: StrategyBlock;
  contextRevision?: string | number;
  onClose: () => void;
  onRemove: () => void | Promise<void>;
  onTaskStarted?: (taskId: string, blockId: string) => void;
}

const EVIDENCE_OPTIONS: Array<{
  mode: LibraryEvidenceSelection['mode'];
  label: string;
  description: string;
}> = [
  {
    mode: 'strategy-default',
    label: 'Strategy default',
    description: 'Use eligible evidence pinned to this strategy. Empty defaults become explicit no evidence.',
  },
  {
    mode: 'selected-only',
    label: 'Selected only',
    description: 'Use only the items you choose below. Nothing else is added silently.',
  },
  {
    mode: 'no-evidence',
    label: 'No evidence',
    description: 'Adapt structure and copy without reviews, ratings, insights, or proof claims.',
  },
];

function evidenceKey(ref: Pick<LibraryEvidenceRef, 'kind' | 'id'>): string {
  return `${ref.kind}:${ref.id}`;
}

function sourceLabel(scope: 'project' | 'system'): string {
  return scope === 'system' ? 'System library' : 'Project library';
}

function evidenceModeLabel(mode: LibraryEvidenceSelection['mode']): string {
  return EVIDENCE_OPTIONS.find((option) => option.mode === mode)?.label ?? mode;
}

export const LibraryAdaptationInspector: React.FC<LibraryAdaptationInspectorProps> = ({
  projectId,
  strategyId,
  block,
  contextRevision,
  onClose,
  onRemove,
  onTaskStarted,
}) => {
  const inspector = useLibraryAdaptationInspector({
    projectId,
    strategyId,
    block,
    contextRevision,
    onTaskStarted,
  });
  const state = inspector.preflight?.state ?? inspector.persisted;
  const selectedKeys = new Set(
    inspector.selection.mode === 'selected-only'
      ? inspector.selection.refs.map(evidenceKey)
      : [],
  );
  const isBusy = inspector.preflightLoading || inspector.adaptLoading || state?.status === 'adapting';
  const selectedOnlyEmpty = inspector.selection.mode === 'selected-only'
    && inspector.selection.refs.length === 0;
  const topology = inspector.preflight?.topology;
  const unresolvedVariables = (inspector.preflight?.variableConflicts ?? []).some(
    (conflict) => !inspector.variableResolutions[conflict.sourceVariableId],
  );
  const blockedByPreflight = Boolean(inspector.preflight?.evidence.blockingReason);
  const blockedByRefreshError = Boolean(inspector.error);

  return (
    <Box
      role="complementary"
      aria-label="Adapt library draft"
      data-testid={STRATEGY_CANVAS.libraryAdaptationInspector}
      sx={{
        width: 430,
        maxWidth: '42vw',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ minHeight: 56, px: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <IconButton
          size="small"
          aria-label="Close adaptation inspector"
          data-testid={STRATEGY_CANVAS.libraryAdaptationClose}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Adapt library draft</Typography>
          <Typography variant="caption" color="text.secondary">
            Place and connect it first, then generate the saved block.
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {state?.status === 'failed' && (
          <Alert severity="error">
            {state.error?.message ?? 'The previous adaptation failed. Review the context and try again.'}
          </Alert>
        )}
        {state?.status === 'stale' && (
          <Alert severity="warning">
            The block placement or strategy context changed. Refresh the review before adapting again.
          </Alert>
        )}
        {state?.status === 'adapting' && (
          <Alert severity="info" icon={<CircularProgress size={18} />}>
            Adapting this block. It will be saved only after the complete result passes validation.
          </Alert>
        )}
        {inspector.error && <Alert severity="error">{inspector.error}</Alert>}

        <Box data-testid={STRATEGY_CANVAS.libraryAdaptationSource}>
          <Typography variant="overline" color="text.secondary">Source blueprint</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={600}>{block.name || 'Library block'}</Typography>
            {state && <Chip size="small" variant="outlined" label={sourceLabel(state.source.scope)} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {state
              ? `Example ${state.source.exampleId} is a reference pattern, not copy to preserve verbatim.`
              : 'Loading the source blueprint…'}
          </Typography>
        </Box>

        <Divider />

        <Box data-testid={STRATEGY_CANVAS.libraryAdaptationTopology}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="overline" color="text.secondary">Placement context</Typography>
            <Tooltip title="Resolve current edges and strategy context again">
              <span>
                <IconButton
                  size="small"
                  aria-label="Refresh placement context"
                  data-testid={STRATEGY_CANVAS.libraryAdaptationRefresh}
                  disabled={isBusy}
                  onClick={() => void inspector.refreshPreflight()}
                >
                  {inspector.preflightLoading
                    ? <CircularProgress size={16} />
                    : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {topology ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={topology.neighbours.replaceAll('-', ' ')} />
                <Typography variant="caption" color="text.secondary">Actual connected edges only</Typography>
              </Stack>
              <Typography variant="body2">
                <b>Previous:</b>{' '}
                {topology.predecessor
                  ? `${topology.predecessor.name} · ${topology.predecessor.blockType}`
                  : 'none — valid for a first or isolated block'}
              </Typography>
              <Typography variant="body2">
                <b>Next:</b>{' '}
                {topology.successor
                  ? `${topology.successor.name} · ${topology.successor.blockType}`
                  : 'none — valid for a last or isolated block'}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Resolving current connections…</Typography>
          )}
        </Box>

        <Divider />

        <FormControl component="fieldset" data-testid={STRATEGY_CANVAS.libraryAdaptationEvidenceMode}>
          <Typography component="legend" variant="overline" color="text.secondary">
            Evidence allowed
          </Typography>
          <RadioGroup
            value={inspector.selection.mode}
            onChange={(event) => inspector.setEvidenceMode(event.target.value as LibraryEvidenceSelection['mode'])}
          >
            {EVIDENCE_OPTIONS.map((option) => (
              <Box key={option.mode} sx={{ mb: 0.5 }}>
                <FormControlLabel
                  value={option.mode}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2" fontWeight={600}>{option.label}</Typography>}
                  disabled={isBusy}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                  {option.description}
                </Typography>
              </Box>
            ))}
          </RadioGroup>

          {inspector.selection.mode === 'selected-only' && (
            <Stack spacing={0.25} sx={{ mt: 1, pl: 1 }}>
              {(inspector.preflight?.availableEvidenceItems ?? []).map((item) => {
                const ref: LibraryEvidenceRef = { kind: item.kind, id: item.id };
                return (
                  <FormControlLabel
                    key={evidenceKey(ref)}
                    control={(
                      <Checkbox
                        size="small"
                        checked={selectedKeys.has(evidenceKey(ref))}
                        onChange={() => inspector.toggleEvidence(ref)}
                        disabled={isBusy}
                        inputProps={{
                          'aria-label': `Use ${item.label}`,
                          'data-testid': STRATEGY_CANVAS.libraryAdaptationEvidenceItem(item.kind, item.id),
                        } as React.InputHTMLAttributes<HTMLInputElement>}
                      />
                    )}
                    label={<Typography variant="body2">{item.label}</Typography>}
                  />
                );
              })}
              {!inspector.preflightLoading
                && (inspector.preflight?.availableEvidenceItems.length ?? 0) === 0 && (
                  <Alert severity="warning">No eligible evidence is available for an explicit selection.</Alert>
                )}
            </Stack>
          )}

          {inspector.preflight?.evidence.fallbackReason === 'strategy-default-empty' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Strategy default is empty, so this run will use no evidence. No proof will be invented.
            </Alert>
          )}
          {inspector.preflight?.evidence.blockingReason && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {inspector.preflight.evidence.blockingReason}
            </Alert>
          )}
          {inspector.preflight && (
            <Stack
              spacing={0.75}
              sx={{ mt: 1 }}
              data-testid={STRATEGY_CANVAS.libraryAdaptationEvidenceSummary}
            >
              <Typography variant="caption" color="text.secondary">
                {`Requested: ${evidenceModeLabel(inspector.preflight.evidence.requestedMode)} · Effective: ${evidenceModeLabel(inspector.preflight.evidence.mode)} · Selected: ${inspector.preflight.evidence.selectedCount} · Resolved: ${inspector.preflight.evidence.resolvedCount}`}
              </Typography>
              {inspector.preflight.evidenceItems.length > 0 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {inspector.preflight.evidenceItems.map((item) => (
                    <Chip
                      key={evidenceKey(item as LibraryEvidenceRef)}
                      size="small"
                      variant="outlined"
                      label={`${item.kind} · ${item.label}`}
                      data-testid={STRATEGY_CANVAS.libraryAdaptationResolvedEvidenceItem(item.kind, item.id)}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No evidence items will be used.
                </Typography>
              )}
            </Stack>
          )}
        </FormControl>

        <Divider />

        <Box>
          <Typography variant="overline" color="text.secondary">AI model</Typography>
          <AvailableModelSelector
            projectId={projectId}
            value={inspector.model}
            onChange={inspector.setModel}
            label="AI model"
            size="small"
            fullWidth
            disabled={isBusy || inspector.modelLoading}
            testId={STRATEGY_CANVAS.libraryAdaptationModel}
          />
        </Box>

        <TextField
          label="Optional adaptation instruction"
          placeholder="For example: make the tone calmer, but keep the decision pattern."
          value={inspector.operatorInstruction}
          onChange={(event) => inspector.setOperatorInstruction(event.target.value)}
          onBlur={() => void inspector.refreshPreflight()}
          multiline
          minRows={3}
          disabled={isBusy}
          inputProps={{ 'data-testid': STRATEGY_CANVAS.libraryAdaptationInstruction }}
          helperText="Applied in addition to the server-owned strategy, topology, variable, theme, and evidence context."
        />

        {inspector.preflight && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`${inspector.preflight.variableMappingCount} variable mappings`} />
            <Chip
              size="small"
              variant="outlined"
              color={inspector.preflight.hasVariableConflicts ? 'warning' : 'default'}
              label={inspector.preflight.hasVariableConflicts ? 'variable review needed' : 'variables compatible'}
            />
            {inspector.preflight.hasAssetIntents && <Chip size="small" variant="outlined" label="asset prompts will be rebuilt" />}
            <Chip
              size="small"
              variant="outlined"
              color={inspector.preflight.hasTargetTheme ? 'default' : 'warning'}
              label={inspector.preflight.hasTargetTheme ? 'target theme included' : 'no target theme'}
            />
          </Stack>
        )}

        {(inspector.preflight?.variableConflicts.length ?? 0) > 0 && (
          <Stack spacing={1.25} data-testid={STRATEGY_CANVAS.libraryAdaptationVariableConflicts}>
            <Alert severity="warning">
              Choose how each incompatible example variable should be handled before adaptation.
            </Alert>
            {inspector.preflight?.variableConflicts.map((conflict) => {
              const current = inspector.variableResolutions[conflict.sourceVariableId];
              const currentValue = current?.action === 'bind'
                ? `bind:${current.targetVariableId}`
                : current?.action ?? '';
              return (
                <TextField
                  key={conflict.sourceVariableId}
                  select
                  size="small"
                  label={conflict.sourceVariableId}
                  value={currentValue}
                  disabled={isBusy}
                  helperText={conflict.reason}
                  inputProps={{
                    'data-testid': STRATEGY_CANVAS.libraryAdaptationVariableResolution(
                      conflict.sourceVariableId,
                    ),
                  }}
                  onChange={(event) => {
                    const value = event.target.value;
                    const resolution: LibraryVariableResolution = value.startsWith('bind:')
                      ? { action: 'bind', targetVariableId: value.slice('bind:'.length) }
                      : { action: value as 'create' | 'drop' };
                    inspector.setVariableResolution(conflict.sourceVariableId, resolution);
                  }}
                >
                  <MenuItem value="" disabled>Select a resolution</MenuItem>
                  {conflict.targetVariableId && (
                    <MenuItem value={`bind:${conflict.targetVariableId}`}>
                      Bind to {conflict.targetVariableId}
                    </MenuItem>
                  )}
                  <MenuItem value="create">Create a new strategy variable</MenuItem>
                  <MenuItem value="drop">Remove this variable dependency</MenuItem>
                </TextField>
              );
            })}
          </Stack>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}
      >
        <Button
          color="error"
          variant="text"
          data-testid={STRATEGY_CANVAS.libraryAdaptationRemove}
          disabled={isBusy}
          onClick={() => void onRemove()}
        >
          Remove draft
        </Button>
        <Button
          variant="contained"
          startIcon={inspector.adaptLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          data-testid={STRATEGY_CANVAS.libraryAdaptationSubmit}
          disabled={isBusy
            || !inspector.preflight
            || !inspector.model
            || selectedOnlyEmpty
            || unresolvedVariables
            || blockedByPreflight
            || blockedByRefreshError}
          onClick={() => void inspector.startAdaptation()}
        >
          Adapt &amp; save block
        </Button>
      </Stack>
    </Box>
  );
};

LibraryAdaptationInspector.displayName = 'LibraryAdaptationInspector';
